import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { PublicationBookingItemModel, PublicationBookingItemRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['publicationBookingItems']

export type PublicationBookingItemFilters = {
  publication_booking_id?: number,
  publication_order_id?: number,
  removed?: 'true' | 'false',
}

type UsePublicationBookingItemsOptions = {
  filters?: PublicationBookingItemFilters,
  performHttpRequests?: boolean,
  requestOptions?: PublicationBookingItemRequestOptions,
}

function usePublicationBookingItems(options: UsePublicationBookingItemsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    publication_booking_id: filterPublicationBookingId,
    publication_order_id: filterPublicationOrderId,
    removed: filterRemoved,
  } = filters

  const {
    updatedEntities: { publicationBookingItems: publicationBookingItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { publicationBookingItems, publicationBookings } = useSelector(reduxState => reduxState.entities)

  const filteredPublicationBookingItems = useMemo(() => {
    const filtered = Object
      .values(publicationBookingItems)
      .filter((publicationBookingItem: PublicationBookingItemModel) => {
        const { previous_booking_id, publication_booking_id, removed } = publicationBookingItem

        const matchPublicationBooking = matchFilterNumber(publication_booking_id, filterPublicationBookingId)
        const matchRemoved = filterRemoved ? removed.toString() === filterRemoved : true

        // @ts-expect-error TS2538
        const previousBooking = publicationBookings[previous_booking_id] || {}
        const matchPreviousBooking = previousBooking
          ? matchFilterNumber(previousBooking.publication_order_id, filterPublicationOrderId)
          : true

        return matchPreviousBooking && matchPublicationBooking && matchRemoved
      })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [publicationBookingItemsUpdatedAt, JSON.stringify(filters)])

  const filteredPublicationBookingItemsCount = filteredPublicationBookingItems.length
  const hasFilteredPublicationBookingItems = !!filteredPublicationBookingItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPublicationBookingItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingPublicationBookingItems } = useReduxAction(
    'publicationBookingItems',
    'loadPublicationBookingItems',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPublicationBookingItems,
    filteredPublicationBookingItemsCount,
    hasFilteredPublicationBookingItems,
    loading: loadingPublicationBookingItems,
  }
}

export type UsePublicationBookingItemsPayload = ReturnType<typeof usePublicationBookingItems>

export default usePublicationBookingItems
